import { createSlice } from '@reduxjs/toolkit'
import name from './name'
import playlistLanding from '../playlist-landing'

const initialState = {
  detail: undefined,
  content: [],
  loading: false,
  back: undefined,
  readTimes: [],
  playlistIds: [],
  /**
   * The playlist this slice is currently bound to, as a full pk.
   *
   * `null` means bound to nothing, which is what the create form binds to. It
   * is the difference between "no playlist is being shown" and "a playlist is
   * being shown but has not arrived yet", and without it a response for a
   * playlist the user has already navigated away from lands in the slice and
   * populates whatever is on screen.
   */
  context: null
}

/**
 * Callers hold the id in both shapes: the detail route gives a bare uuid, while
 * the content page carries the full pk in its query string.
 */
const toPk = id => {
  if (!id) return null

  return String(id).startsWith('playlist#') ? String(id) : `playlist#${id}`
}

export const content = createSlice({
  name,
  initialState,
  reducers: {
    setContext: (state, { payload }) => {
      state.context = toPk(payload)
    },
    setDetail: (state, { payload }) => {
      // A response for a playlist this view is not showing — the user left
      // before it arrived. Dropping it is what stops a half-loaded playlist
      // repopulating the next screen, the create form above all.
      if (!state.context || payload.playlist?.pk !== state.context) return

      state.detail = payload.playlist
      state.playlistIds = [payload.playlist.pk]
    },
    /**
     * The content list as the api returned it. Separate from `setContent`
     * because that one carries the user's own edits — reordering, adding,
     * removing — and must always be honoured.
     *
     * The response has no playlist id to match on, so this can only ask
     * whether any playlist is being shown at all. That covers the create form,
     * which binds to nothing.
     */
    setFetchedContent: (state, { payload }) => {
      if (!state.context) return

      state.content = payload.content
      state.loading = false
    },
    setContent: (state, { payload }) => {
      state.content = payload.content
      state.loading = false
    },
    setReadTimes: (state, { payload }) => {
      state.readTimes = payload
    },
    addContent: (state, { payload }) => {
      state.content.push(payload)
    },
    setLoading: (state, { payload }) => {
      state.loading = true
    },
    toggleLike: (state, { payload }) => {
      state.detail.likeCount += payload.status === 'liked' ? 1 : -1
      state.detail.hasLiked = payload.status === 'liked'
    },
    setBack: (state, { payload }) => {
      state.back = payload
    },
    reset: state => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(playlistLanding.actions.setResults, (state, { payload }) => {
        if (!payload.items) return
        const playlistIds = payload.items.map(playlist => {
          return playlist.pk
        })
        if (playlistIds.length) {
          state.playlistIds = playlistIds
        }
      })
  }
})

export const {
  setBack,
  setContext,
  setDetail,
  setContent,
  setFetchedContent,
  addContent,
  setLoading,
  toggleLike,
  setReadTimes,
  reset
} = content.actions

export default content.reducer
